import { Box, Button, Card, CardActionArea, Grid, Slider, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Vex from "vexflow";

export interface ISheetMusicNoteTrainerSettings {
    range: [number, number];
    keyId: string;
}

interface IProps {
    onSuccess: (settings: ISheetMusicNoteTrainerSettings) => void;
}

const KEYS = [
    { id: 'C', label: 'C / Am', sig: '♮' },
    { id: 'G', label: 'G / Em', sig: '1♯' },
    { id: 'D', label: 'D / Bm', sig: '2♯' },
    { id: 'A', label: 'A / F♯m', sig: '3♯' },
    { id: 'E', label: 'E / C♯m', sig: '4♯' },
    { id: 'B', label: 'B / G♯m', sig: '5♯' },
    { id: 'F', label: 'F / Dm', sig: '1♭' },
    { id: 'Bb', label: 'B♭ / Gm', sig: '2♭' },
    { id: 'Eb', label: 'E♭ / Cm', sig: '3♭' },
    { id: 'Ab', label: 'A♭ / Fm', sig: '4♭' },
    { id: 'Db', label: 'D♭ / B♭m', sig: '5♭' },
];

export const MIN_MIDI = 41; // F2
export const MAX_MIDI = 84; // C6

export const SheetMusicNoteTrainerSettings: React.FC<IProps> = ({ onSuccess }) => {
    const [selectedKey, setSelectedKey] = useState<string>('C');
    const [range, setRange] = useState<[number, number]>([41, 84]);
    const scoreRef = useRef<HTMLDivElement>(null);

    const handleRangeChange = (event: Event, newValue: number | number[]) => {
        setRange(newValue as [number, number]);
    };

    useEffect(() => {
        if (!scoreRef.current) return;
        scoreRef.current.innerHTML = '';

        const VF = Vex.Flow;
        const width = 800;
        const height = 240;

        const renderer = new VF.Renderer(scoreRef.current, VF.Renderer.Backends.SVG);
        renderer.resize(width, height);
        
        // Делаем SVG адаптивным под ширину контейнера
        const svg = scoreRef.current.querySelector('svg');
        if (svg) {
            svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
            svg.style.width = '100%';
            svg.style.height = 'auto';
        }
        
        const context = renderer.getContext();

        const staveX = 20;
        const staveWidth = width - 40;

        // Создаем скрипичный и басовый ключи с выбранной тональностью
        const trebleStave = new VF.Stave(staveX, 20, staveWidth).addClef("treble").addKeySignature(selectedKey);
        const bassStave = new VF.Stave(staveX, 120, staveWidth).addClef("bass").addKeySignature(selectedKey);
        
        trebleStave.setContext(context).draw();
        bassStave.setContext(context).draw();

        // Рисуем связки для Grand Staff (двойного стана)
        new VF.StaveConnector(trebleStave, bassStave).setType(VF.StaveConnector.type.BRACE).setContext(context).draw();
        new VF.StaveConnector(trebleStave, bassStave).setType(VF.StaveConnector.type.SINGLE_LEFT).setContext(context).draw();
        new VF.StaveConnector(trebleStave, bassStave).setType(VF.StaveConnector.type.SINGLE_RIGHT).setContext(context).draw();

        // Диатонические ноты для визуализации
        const bassKeys = [
            { key: "f/2", midi: 41 }, { key: "g/2", midi: 43 }, { key: "a/2", midi: 45 }, { key: "b/2", midi: 47 },
            { key: "c/3", midi: 48 }, { key: "d/3", midi: 50 }, { key: "e/3", midi: 52 }, { key: "f/3", midi: 53 },
            { key: "g/3", midi: 55 }, { key: "a/3", midi: 57 }, { key: "b/3", midi: 59 }
        ];

        const trebleKeys = [
            { key: "c/4", midi: 60 }, { key: "d/4", midi: 62 }, { key: "e/4", midi: 64 }, { key: "f/4", midi: 65 },
            { key: "g/4", midi: 67 }, { key: "a/4", midi: 69 }, { key: "b/4", midi: 71 },
            { key: "c/5", midi: 72 }, { key: "d/5", midi: 74 }, { key: "e/5", midi: 76 }, { key: "f/5", midi: 77 },
            { key: "g/5", midi: 79 }, { key: "a/5", midi: 81 }, { key: "b/5", midi: 83 }, { key: "c/6", midi: 84 }
        ];

        const createNote = (keyData: { key: string, midi: number }, clef: string) => {
            const staveNote = new VF.StaveNote({ keys: [keyData.key], duration: "w", clef: clef });
            const inRange = keyData.midi >= range[0] && keyData.midi <= range[1];
            const color = inRange ? "#1976d2" : "#e0e0e0";
            staveNote.setStyle({ fillStyle: color, strokeStyle: color });
            return staveNote;
        };

        const bassNotes = bassKeys.map(k => createNote(k, "bass"));
        const trebleNotes = trebleKeys.map(k => createNote(k, "treble"));

        const trebleVoice = new VF.Voice({ num_beats: trebleNotes.length * 4, beat_value: 4 }).addTickables(trebleNotes);
        const bassVoice = new VF.Voice({ num_beats: bassNotes.length * 4, beat_value: 4 }).addTickables(bassNotes);

        new VF.Formatter().joinVoices([trebleVoice]).format([trebleVoice], staveWidth - 100);
        new VF.Formatter().joinVoices([bassVoice]).format([bassVoice], staveWidth - 100);

        trebleVoice.draw(context, trebleStave);
        bassVoice.draw(context, bassStave);
    }, [range, selectedKey]);

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
            <Typography variant="h4" mb={{ xs: 2, sm: 4 }} textAlign="center" fontSize={{ xs: '1.5rem', sm: '2.125rem' }}>Настройки тренировки</Typography>

            <Typography variant="h6" mb={2} fontSize={{ xs: '1rem', sm: '1.25rem' }}>1. Выберите тональность</Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }} mb={4}>
                {KEYS.map(k => (
                    <Grid item xs={4} sm={3} md={2} key={k.id}>
                        <Card 
                            sx={{ 
                                border: selectedKey === k.id ? '2px solid #1976d2' : '2px solid transparent',
                                backgroundColor: selectedKey === k.id ? '#e3f2fd' : 'white',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <CardActionArea onClick={() => setSelectedKey(k.id)} sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                                <Typography variant="body2" fontWeight="bold" fontSize={{ xs: '0.8rem', sm: '1rem' }}>{k.label}</Typography>
                                <Typography variant="h6" color="text.secondary" mt={0.5} fontSize={{ xs: '1rem', sm: '1.25rem' }}>{k.sig}</Typography>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Typography variant="h6" mb={2} fontSize={{ xs: '1rem', sm: '1.25rem' }}>2. Выберите диапазон нот</Typography>
            <Box mb={2} sx={{ pointerEvents: 'none', display: 'flex', justifyContent: 'center', width: '100%' }}>
                <div ref={scoreRef} style={{ width: '100%', maxWidth: 800 }} />
            </Box>
            <Box px={{ xs: 2, sm: 3 }} mb={5}>
                <Slider value={range} onChange={handleRangeChange} valueLabelDisplay="auto" min={MIN_MIDI} max={MAX_MIDI} disableSwap />
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">От: MIDI {range[0]}</Typography>
                    <Typography variant="body2" color="text.secondary">До: MIDI {range[1]}</Typography>
                </Box>
            </Box>

            <Box display="flex" justifyContent="center">
                <Button variant="contained" size="large" onClick={() => onSuccess({ range, keyId: selectedKey })}>Начать тренировку</Button>
            </Box>
        </Box>
    );
}